import { lazy, Suspense } from "react";
import { Route } from "react-router-dom";

// Landing page — eager so the first paint isn't delayed by an extra chunk fetch
import PublicLanding from "@/features/storefront/pages/PublicLanding";
import Index from "@/shared/pages/Index";

// Order-flow pages — lazy loaded; only needed after the user taps "View Menu"
const MenuPage = lazy(() => import("@/features/storefront/pages/MenuPage"));
const MenuItemPage = lazy(() => import("@/features/storefront/pages/MenuItemPage"));
const CartPage = lazy(() => import("@/features/storefront/pages/CartPage"));
const PublicOrderLayout = lazy(() =>
  import("@/features/storefront/layouts/PublicOrderLayout").then((m) => ({ default: m.PublicOrderLayout }))
);

// Secondary paths — lazy loaded, only fetched when the user navigates there
const CheckoutPage = lazy(() => import("@/features/storefront/pages/CheckoutPage"));
const OrderConfirmationPage = lazy(() =>
  import("@/features/storefront/pages/OrderConfirmationPage")
);
const OrderDetailsPage = lazy(() => import("@/features/storefront/pages/OrderDetailsPage"));
const MyOrdersPage = lazy(() => import("@/features/storefront/pages/MyOrdersPage"));
const OrderTrackingPage = lazy(() =>
  import("@/features/notifications").then((m) => ({ default: m.OrderTrackingPage }))
);
const LegalPage = lazy(() => import("@/features/storefront/pages/LegalPage"));
const ReservationPage = lazy(() => import("@/features/storefront/pages/ReservationPage"));
const PlatformLegalPage = lazy(() => import("@/shared/pages/PlatformLegalPage"));

/**
 * Public Routes Configuration
 *
 * These routes are accessible without authentication.
 * Primary order-flow pages (Index, PublicLanding, Menu, Cart) are eagerly
 * loaded. Everything else is lazy to keep the initial bundle small.
 */
export const publicRoutes = (
    <>
        {/* Public landing page */}
        <Route path="/" element={<Index />} />

        {/* Public order tracking - token-only URL, no auth */}
        <Route
            path="/track/:trackingToken"
            element={<Suspense fallback={null}><OrderTrackingPage /></Suspense>}
        />

        {/* Public restaurant pages - /:slug for landing, /:slug/menu for menu flow */}
        <Route path="/:restaurantId" element={<PublicLanding />} />

        {/* Order flow: menu, item page - shared cart context */}
        <Route path="/:restaurantId/menu" element={<Suspense fallback={null}><PublicOrderLayout /></Suspense>}>
            <Route index element={<Suspense fallback={null}><MenuPage /></Suspense>} />
            <Route path="item/:itemId" element={<Suspense fallback={null}><MenuItemPage /></Suspense>} />
            <Route
                path="checkout"
                element={<Suspense fallback={null}><CheckoutPage /></Suspense>}
            />
            <Route
                path="confirmation"
                element={<Suspense fallback={null}><OrderConfirmationPage /></Suspense>}
            />
        </Route>

        {/* Cart - top-level under restaurant for /rasam/cart */}
        <Route path="/:restaurantId/cart" element={<Suspense fallback={null}><PublicOrderLayout /></Suspense>}>
            <Route index element={<Suspense fallback={null}><CartPage /></Suspense>} />
        </Route>

        {/* Previously placed orders (local to this device). */}
        <Route path="/:restaurantId/orders" element={<Suspense fallback={null}><PublicOrderLayout /></Suspense>}>
            <Route index element={<Suspense fallback={null}><MyOrdersPage /></Suspense>} />
            <Route
                path=":orderId"
                element={<Suspense fallback={null}><OrderDetailsPage /></Suspense>}
            />
        </Route>

        {/* Reservation page */}
        <Route
            path="/:restaurantId/reserve"
            element={<Suspense fallback={null}><ReservationPage /></Suspense>}
        />

        {/* Platform legal pages — Impressum, Datenschutz, AGB */}
        <Route
            path="/legal/:type"
            element={<Suspense fallback={null}><PlatformLegalPage /></Suspense>}
        />

        {/* Restaurant-level legal pages */}
        <Route
            path="/:restaurantId/legal/:legalType"
            element={<Suspense fallback={null}><LegalPage /></Suspense>}
        />
    </>
);
